import { useEffect, useMemo, useState } from "react";
import { stallUnresolvedPromises } from "./async";

/**
 *
 * hook to handle the common scenario of asynchronously re-fetching data whenever
 * some input parameters change. Parameters should be json serializable, as that is used
 * for diffing to trigger a refetch.
 *
 *
 * This hook provides the following helpful state, and coordinates some of the harder
 * to handle nuances of race conditions if the input parameters change while there is an
 * existing request already in progress. (This is often the source of bugs when naively implemented
 * as a `useEffect` that fetches and sets state with the input parameter)
 *
 * Example data flow
 * 
 *  Input: 
 *      1  |-------------- - - - - - - - - - |
 *         ^fetch         ^cancelled         ^ response ignored
 *          loading true
 *      2                 |---------------|
 *                                        ^response set, loading false
 * 
 *      3                                            |-----------------------X
 *                                                   ^ loading true          ^ error response, set error
 * 
 * 
 * @param {*} parameters - json stringifiable parameters. Whenever changed, 
 *                         will trigger a call to fetch
 * @param {function} fetch - function that returns a promise and takes parameters 
 *                           type as its only argument. THIS FUNCTION IS MEMOIZED 
 *                           AND NEVER UPDATED ACROSS RENDERS. This is somewhat of a cludge 
 *                           to correctly track calls to the function across renders. This 
 *                           code could be improved to be more flexible, however the expectation
 *                           is that all input parameters are passed in as part of parameters, 
 *                           so this function should have no external dependencies anyways
 * @returns {{ loading: boolean, error?: any, response: any }} - "loading" true if a query is in progress, 
 *                                                               "error" has value if the fetch function fails. 
 *                                                               "response" is the eventual return value of fetch
 * */
export function withRemoteQuery({ parameters, fetch }) {
  const [response, setResponse] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const fetcher = useMemo(() => stallUnresolvedPromises(fetch), []);

  useEffect(() => {
    setLoading(true);
    setResponse(undefined);
    setError(undefined);
    fetcher(parameters)
      .then((resp) => {
        setLoading(false);
        setResponse(resp);
      })
      .catch((ex) => {
        setLoading(false);
        setError(ex);
      });
  }, [JSON.stringify(parameters)]);


  return {
    response, loading, error
  }
}
